import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import '../../styles/pages/AdminPages.css';

function MoneyPage() {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [activeTab, setActiveTab] = useState('day'); // day, week, month, quarter, half, year
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 초기 날짜 설정 (오늘)
  useEffect(() => {
    const today = new Date();
    const formatted = today.toISOString().split('T')[0];
    setStartDate(formatted);
    setEndDate(formatted);
  }, []);

  // 탭 변경 시 자동으로 날짜 설정
  useEffect(() => {
    const end = new Date();
    let start = new Date();
    
    switch (activeTab) {
      case 'day':
        // 오늘
        start = new Date(end);
        break;
      case 'week':
        // 최근 7일
        start.setDate(end.getDate() - 6);
        break;
      case 'month':
        // 최근 30일
        start.setDate(end.getDate() - 29);
        break;
      case 'quarter':
        // 최근 3개월
        start.setMonth(end.getMonth() - 3);
        break;
      case 'half':
        // 최근 6개월
        start.setMonth(end.getMonth() - 6);
        break;
      case 'year':
        // 최근 1년
        start.setFullYear(end.getFullYear() - 1);
        break;
      default:
        break;
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, [activeTab]);

  // 통계 조회
  const fetchStats = async () => {
    if (!startDate || !endDate) {
      setError('시작일과 종료일을 선택해주세요.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const url = `${API_URL}/api/admin/revenue/stats?startDate=${startDate}&endDate=${endDate}&period=${activeTab}`;
      const res = await fetch(url, {
        credentials: 'include'
      });

      const data = await res.json();
      
      if (data.ok) {
        setStats(data.stats);
      } else {
        setError(data.message || '통계 조회에 실패했습니다.');
      }
    } catch (err) {
      console.error('fetchStats error:', err);
      setError('통계 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 날짜 변경 시 자동 조회
  useEffect(() => {
    if (startDate && endDate) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // 숫자 포맷팅
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return Math.round(num).toLocaleString();
  };

  return (
    <AdminLayout title="매출 관리">
      {/* 탭 */}
      <div style={{ 
        display: 'flex', 
        gap: 8, 
        marginBottom: 24,
        borderBottom: '2px solid var(--admin-border-color)',
        paddingBottom: 8
      }}>
        {[
          { key: 'day', label: '일별' },
          { key: 'week', label: '주별' },
          { key: 'month', label: '월별' },
          { key: 'quarter', label: '분기별' },
          { key: 'half', label: '반기별' },
          { key: 'year', label: '1년별' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 20px',
              background: activeTab === tab.key ? 'var(--admin-color-primary)' : 'var(--admin-bg-panel)',
              color: activeTab === tab.key ? 'var(--color-white)' : 'var(--admin-text-body)',
              border: `1px solid ${activeTab === tab.key ? 'var(--admin-color-primary)' : 'var(--admin-border-color)'}`,
              borderRadius: 'var(--admin-input-radius)',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: activeTab === tab.key ? 600 : 400,
              transition: 'all 0.2s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 날짜 선택 */}
      <div style={{ 
        display: 'flex', 
        gap: 16, 
        alignItems: 'center',
        marginBottom: 32,
        padding: 20,
        background: 'var(--admin-bg-hover)',
        borderRadius: 'var(--admin-card-radius)',
        border: '1px solid var(--admin-border-color)'
      }}>
        <label style={{ fontWeight: 600, color: 'var(--admin-text-main)' }}>기간:</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid var(--admin-border-color)',
            borderRadius: 'var(--admin-input-radius)',
            fontSize: 14
          }}
        />
        <span style={{ color: 'var(--admin-text-sub)' }}>~</span>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid var(--admin-border-color)',
            borderRadius: 'var(--admin-input-radius)',
            fontSize: 14
          }}
        />
      </div>

      {/* 로딩 및 에러 */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--admin-text-body)' }}>
          통계를 불러오는 중...
        </div>
      )}

      {error && (
        <div style={{ 
          padding: 16, 
          background: '#fee', 
          color: 'var(--admin-color-danger)',
          borderRadius: 'var(--admin-card-radius)',
          marginBottom: 24,
          border: '1px solid var(--admin-color-danger)'
        }}>
          {error}
        </div>
      )}

      {/* 통계 카드 */}
      {stats && !loading && (
        <div>
          {/* 기본 통계 섹션 */}
          <h2 style={{ 
            fontSize: 20, 
            fontWeight: 600, 
            marginBottom: 20,
            color: 'var(--admin-text-main)',
            borderBottom: '2px solid var(--admin-color-primary)',
            paddingBottom: 8
          }}>
            기본 통계
          </h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 20,
            marginBottom: 40
          }}>
            {/* 1. 충전 포인트 */}
            <div style={{
              padding: 24,
              background: 'var(--admin-bg-panel)',
              border: '1px solid var(--admin-border-color)',
              borderRadius: 'var(--admin-card-radius)',
              boxShadow: 'var(--admin-shadow-card)'
            }}>
              <div style={{ 
                fontSize: 14, 
                color: 'var(--admin-text-sub)', 
                marginBottom: 8,
                fontWeight: 600
              }}>
                해당 기간 충전 포인트
              </div>
              <div style={{ 
                fontSize: 24, 
                fontWeight: 700, 
                color: 'var(--admin-color-primary)',
                marginBottom: 4
              }}>
                {formatNumber(stats.chargedPoints)}pt
              </div>
              <div style={{ fontSize: 13, color: 'var(--admin-text-muted)' }}>
                ({formatNumber(stats.chargedAmount)}원)
              </div>
            </div>

            {/* 2. 프로젝트 결제 포인트 */}
            <div style={{
              padding: 24,
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ 
                fontSize: 14, 
                color: '#666', 
                marginBottom: 8,
                fontWeight: 600
              }}>
                해당 기간 프로젝트 결제 포인트
              </div>
              <div style={{ 
                fontSize: 24, 
                fontWeight: 700, 
                color: '#9c27b0'
              }}>
                {formatNumber(stats.projectDepositPoints)}pt
              </div>
            </div>

            {/* 3. 환전/환불 완료 포인트 */}
            <div style={{
              padding: 24,
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ 
                fontSize: 14, 
                color: '#666', 
                marginBottom: 8,
                fontWeight: 600
              }}>
                해당 기간 환전/환불 완료 포인트
              </div>
              <div style={{ 
                fontSize: 24, 
                fontWeight: 700, 
                color: '#d32f2f'
              }}>
                {formatNumber(stats.totalRefundExchangePoints)}pt
              </div>
            </div>

            {/* 4. 광고주 보유 포인트 */}
            <div style={{
              padding: 24,
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ 
                fontSize: 14, 
                color: '#666', 
                marginBottom: 8,
                fontWeight: 600
              }}>
                모든 광고주 보유 포인트 총합
              </div>
              <div style={{ 
                fontSize: 24, 
                fontWeight: 700, 
                color: '#2e7d32',
                marginBottom: 4
              }}>
                {formatNumber(stats.totalAdvertiserPoints)}pt
              </div>
              <div style={{ fontSize: 13, color: '#999' }}>
                ({formatNumber(stats.totalAdvertiserAmount)}원)
              </div>
            </div>

            {/* 5. 인플루언서 보유 포인트 */}
            <div style={{
              padding: 24,
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ 
                fontSize: 14, 
                color: '#666', 
                marginBottom: 8,
                fontWeight: 600
              }}>
                모든 인플루언서 보유 포인트 총합
              </div>
              <div style={{ 
                fontSize: 24, 
                fontWeight: 700, 
                color: '#f57c00',
                marginBottom: 4
              }}>
                {formatNumber(stats.totalInfluencerPoints)}pt
              </div>
              <div style={{ fontSize: 13, color: '#999' }}>
                ({formatNumber(stats.totalInfluencerAmount)}원)
              </div>
            </div>
          </div>

          {/* 매출 섹션 */}
          <h2 style={{ 
            fontSize: 20, 
            fontWeight: 600, 
            marginBottom: 20,
            color: '#333',
            borderBottom: '2px solid #d32f2f',
            paddingBottom: 8
          }}>
            매출 통계
          </h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: 24
          }}>
            {/* 충전 매출 */}
            <div style={{
              padding: 32,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 12,
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              color: '#fff'
            }}>
              <div style={{ 
                fontSize: 16, 
                marginBottom: 12,
                fontWeight: 600,
                opacity: 0.9
              }}>
                충전 매출
              </div>
              <div style={{ 
                fontSize: 36, 
                fontWeight: 800,
                marginBottom: 8
              }}>
                {formatNumber(stats.chargeSales)}원
              </div>
              <div style={{ fontSize: 13, opacity: 0.8 }}>
                충전 포인트 × 110%
              </div>
            </div>

            {/* 실제 이익 */}
            <div style={{
              padding: 32,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              borderRadius: 12,
              boxShadow: '0 4px 12px rgba(245, 87, 108, 0.4)',
              color: '#fff'
            }}>
              <div style={{ 
                fontSize: 16, 
                marginBottom: 12,
                fontWeight: 600,
                opacity: 0.9
              }}>
                실제 이익
              </div>
              <div style={{ 
                fontSize: 36, 
                fontWeight: 800,
                marginBottom: 8
              }}>
                {formatNumber(stats.actualProfit)}원
              </div>
              <div style={{ fontSize: 13, opacity: 0.8 }}>
                (운영비 + (리워드+CPS) × 3%) × 110%
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default MoneyPage;
